import React from 'react';
import ViewerStore from '../stores/ViewerStore';
import * as Inovx from '@shared';
import { DropDownListComponent, ChangeEventArgs } from '@syncfusion/ej2-react-dropdowns';

import FacilityStore from '../stores/FacilityStore';
import assetActions from '../actions/AssetActions';
const { useState, useEffect, useRef, useMemo } = React;

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

type PlacementStatus = 'All' | 'RepresentedIn3D' | 'RepresentedIn2D' | 'ManuallyTagged';

interface AssetTagSearchFilterProps {
    includeViewPlacementFilter: boolean;
}

const getDefaultFilter = (): IAssetFilter => ({
    NameFilter: '',
    AssetClassFilter: [],
    PlacementStatusFilter: 'All',
    QueryRules: null,
    QueryApiRules: null,
    QueryOntologyID: null,
});

const AssetTagSearchFilter = React.forwardRef((props: AssetTagSearchFilterProps, ref) => {
    const [facilityAssets, setFacilityAssets] = useState<IFacilityAssetsData>(FacilityStore.getFacilityAssetsData());
    const [viewId, setViewId] = useState<string>('');
    const [filter, setFilter] = useState<IAssetFilter>(ViewerStore.getAssetTagsFilter());
    const fields: { [key: string]: string } = { text: 'text', value: 'id' };
    const assetPlacementOptions = useMemo(
        () => [
            { id: 'All', text: InovxLoader.t('facility:assetTagFilter.allAssets') },
            { id: 'RepresentedIn3D', text: InovxLoader.t('facility:assetTagFilter.representedIn3D') },
            { id: 'RepresentedIn2D', text: InovxLoader.t('facility:assetTagFilter.representedIn2D') },
            { id: 'ManuallyTagged', text: InovxLoader.t('facility:assetTagFilter.manuallyTagged') },
        ],
        []
    );

    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const filterIsSet = useRef(false);
    const updateFilterTimer = useRef<number | null>(null);

    // Build the default filter once on mount (using current store snapshot)
    useEffect(() => {
        const handleFacilityStoreChange = () => {
            if (!isMountedRef.current) return;
            updateFacilityState();
        };
        const handleViewerStoreChange = () => {
            if (!isMountedRef.current) return;
            updateViewerState();
        };

        // Check if a filter already exists in the store
        const existingFilter = ViewerStore.getAssetTagsFilter();
        
        // If filter exists with AssetClassFilter, use it and re-apply to ensure filteredAssets is populated
        // Otherwise, initialize with default filter
        if (existingFilter && existingFilter.AssetClassFilter && existingFilter.AssetClassFilter.length > 0) {
            // Re-apply existing filter to populate filteredAssets in case we're mounting in a new viewer
            updateFilter(existingFilter);
        } else {
            // No filter exists or AssetClassFilter is empty, initialize with defaults
            const assetClasses = facilityAssets?.Facility?.AssetClasses || [];
            const allClassIds = ['select_all', ...assetClasses.map((ac: any) => ac.ID)];
            const defaultFilter = getDefaultFilters(allClassIds);
            updateFilter(defaultFilter);
        }

        FacilityStore.addChangeListener(handleFacilityStoreChange);
        ViewerStore.addChangeListener(handleViewerStoreChange);
        return () => {
            FacilityStore.removeChangeListener(handleFacilityStoreChange);
            ViewerStore.removeChangeListener(handleViewerStoreChange);

            // clear any pending filter update timer
            if (updateFilterTimer.current != null) {
                clearTimeout(updateFilterTimer.current);
                updateFilterTimer.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateViewerState = () => {
        setViewId(ViewerStore.getSelectedViewImage()?.ID ?? '');
        setFilter(ViewerStore.getAssetTagsFilter());
        filterIsSet.current = true;
    };

    const updateFacilityState = () => {
        setFacilityAssets(prevState => {
            const newState = FacilityStore.getFacilityAssetsData();
            if (!newState || !prevState) return newState;
            // shallow-change check on the big buckets
            return (newState.Facility?.ID !== prevState.Facility?.ID ||
                newState.Assets !== prevState.Assets ||
                newState.NativeAssets !== prevState.NativeAssets ||
                newState.AssetTags !== prevState.AssetTags)
                ? newState
                : prevState;
        });
    };

    // refresh filter when active view or assets change (after initial mount pass)
    useEffect(() => {
        if (!filterIsSet.current) return;
        updateFilter(filter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewId, facilityAssets]);

    const onPlacementChange = (args: ChangeEventArgs) => {
        if (args?.isInteracted) {
            filterByPlacementStatus(args.value as PlacementStatus);
        }
    };

    const filterByName = (name: string) => {
        const updatedFilter: IAssetFilter = { ...filter, NameFilter: name };
        updateFilter(updatedFilter);
    };

    const filterByPlacementStatus = (placementStatus: PlacementStatus) => {
        const updatedFilter: IAssetFilter = { ...filter, PlacementStatusFilter: placementStatus };
        updateFilter(updatedFilter);
    };

    const getDefaultFilters = (classFilter: string[]) => {
        const searchParams = new URLSearchParams(window.location.search);
        const assetId = searchParams.get('assetId') || '';
        const filterVal = getDefaultFilter();

        if (assetId) {
            // If page is reloaded with an assetId in the URL, show all
            filterVal.PlacementStatusFilter = 'All';
        }

        if (!props.includeViewPlacementFilter) {
            filterVal.PlacementStatusFilter = 'All';
        }

        filterVal.AssetClassFilter = classFilter;
        return filterVal;
    };

    const updateFilter = (filterVal: IAssetFilter) => {
        // reflect immediately in local state for controlled inputs
        setFilter(filterVal);

        // cancel any pending dispatch and schedule a fresh one
        if (updateFilterTimer.current != null) clearTimeout(updateFilterTimer.current);
        updateFilterTimer.current = window.setTimeout(() => {
            assetActions.updateAssetSearchFilter(filterVal);
            updateFilterTimer.current = null;
        }, 0);
    };

    const assetPlacement = useMemo(
        () => (props.includeViewPlacementFilter ? assetPlacementOptions : assetPlacementOptions.filter(i => i.id !== 'InCurrentView')),
        [assetPlacementOptions, props.includeViewPlacementFilter]
    );

    const getTitle = (id = '') => {
        return (assetPlacement.find((item: any) => item.id === id)?.text as string) || '';
    };

    return (
        <div className="asset-filter">
            <div className="d-flex ctm-dropdown">
                <span title={getTitle(filter.PlacementStatusFilter)} className="w-100">
                    <DropDownListComponent
                        dataSource={assetPlacement}
                        fields={fields}
                        change={onPlacementChange}
                        placeholder={InovxLoader.t('facility:assetTagFilter.selectPlacement')}
                        value={filter.PlacementStatusFilter}
                        popupHeight="220px"
                    />
                </span>
            </div>

            <div className="d-flex tag-search mt-4px mb-4px">
                <div className="search-box w-100">
                    <div className="search-addon">
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/search_light.png`} />
                    </div>
                    <input
                        onChange={e => filterByName(e.target.value)}
                        placeholder={InovxLoader.t('facility:assetTagFilter.searchPlaceholder')}
                        type="text"
                        className="form-control"
                        value={filter.NameFilter || ''}
                    />
                    <div className="search-addon">
                        {filter.NameFilter ? (
                            <button title={InovxLoader.t('facility:assetTagFilter.clear')} className="no-btn" onClick={() => filterByName('')}>
                                <img className="h-12px" src={`${UrlUtilities.getBaseApplicationUrl()}/images/tag/close.png`} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default AssetTagSearchFilter;
